/**
 * Unfiltered Explore is a shop window, not an upload log.
 *
 * Default catalog (`scope: 'all'`) used to fall through to `created_at DESC`
 * because almost nobody has ratings yet. The last Kia Soul listed then filled
 * the first screen and buried the Fortuner / pickups that actually sell the
 * market. This rank is the merchandising order: premium, then pickups, then
 * the rest, with price and year inside each band.
 *
 * Nearby with a real origin stays distance-first from `search_vehicles`.
 * This comparator is for the country catalog and for the nearby→country fallback.
 */
import type { VehicleCategory } from '../types/vehicle.types'

export const MARKETPLACE_SHOPPER_CATEGORY_RANK = {
  luxury: 0,
  sports: 1,
  pickup: 2,
  suv: 3,
  recreational: 4,
  van: 5,
  microbus: 6,
  sedan: 7,
  compact: 8,
  heavy_duty: 9,
  motorcycle: 10,
} as const satisfies Record<VehicleCategory, number>

const UNKNOWN_CATEGORY_RANK = 99

export function marketplaceShopperCategoryRank(
  category: VehicleCategory | null | undefined,
): number {
  if (!category) return UNKNOWN_CATEGORY_RANK
  return MARKETPLACE_SHOPPER_CATEGORY_RANK[category] ?? UNKNOWN_CATEGORY_RANK
}

export interface MarketplaceShopperRankable {
  category?: VehicleCategory | null
  dailyRateCents?: number | null
  year?: number | null
  ratingAvg?: number | null
  /** Open Trust & Safety signals. Higher sinks the listing; 0 is clean. */
  shadowRankPenalty?: number | null
}

export function compareMarketplaceShopperRank(
  a: MarketplaceShopperRankable,
  b: MarketplaceShopperRankable,
): number {
  const penaltyDelta = (a.shadowRankPenalty ?? 0) - (b.shadowRankPenalty ?? 0)
  if (penaltyDelta !== 0) return penaltyDelta

  const categoryDelta =
    marketplaceShopperCategoryRank(a.category) - marketplaceShopperCategoryRank(b.category)
  if (categoryDelta !== 0) return categoryDelta

  const priceDelta = (b.dailyRateCents ?? 0) - (a.dailyRateCents ?? 0)
  if (priceDelta !== 0) return priceDelta

  const yearDelta = (b.year ?? 0) - (a.year ?? 0)
  if (yearDelta !== 0) return yearDelta

  return (b.ratingAvg ?? 0) - (a.ratingAvg ?? 0)
}

export function sortMarketplaceShopperRank<T extends MarketplaceShopperRankable>(
  vehicles: readonly T[],
): T[] {
  return [...vehicles].sort(compareMarketplaceShopperRank)
}
